import { useState, useEffect } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";

/**
 * Reusable SKU thumbnail. Single source of truth = the SKU's stored image_url.
 * - Lazy loaded (loading="lazy")
 * - Click to open a lightbox preview
 * - Falls back to "No Image" placeholder if the URL is missing or the image fails to load
 * - Size prop keeps it small & compact for tables (default 56px)
 */
export function SkuImage({ url, alt = "", size = 56, className = "" }) {
  const [err, setErr] = useState(false);
  const [open, setOpen] = useState(false);
  const box = { width: size, height: size, minWidth: size };

  if (!url || err) {
    return (
      <div
        className={`inline-flex items-center justify-center rounded border border-dashed border-slate-200 bg-slate-50 text-[9px] uppercase tracking-widest text-slate-400 font-mono ${className}`}
        style={box}
        data-testid="sku-image-placeholder"
      >
        No Img
      </div>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`inline-block rounded overflow-hidden border border-slate-200 bg-white hover:ring-2 hover:ring-sky-400 transition ${className}`}
        style={box}
        data-testid="sku-image-thumb"
        title="Click to enlarge"
      >
        <img
          src={url}
          alt={alt}
          loading="lazy"
          referrerPolicy="no-referrer"
          onError={() => setErr(true)}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <div className="text-xs text-slate-500 font-mono mb-2 truncate">{alt || url}</div>
          <img
            src={url}
            alt={alt}
            className="w-full max-h-[70vh] object-contain rounded bg-slate-50"
            onError={() => setErr(true)}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * Small toggle switch driven by localStorage so each page remembers its own preference.
 * Usage:
 *   const [showPhoto, ToggleUI] = useShowPhoto("master-sku");
 *   {showPhoto && <td>...image...</td>}
 */
export function useShowPhoto(scope = "default") {
  const key = `showPhoto:${scope}`;
  const [on, setOn] = useState(false);
  useEffect(() => {
    const saved = localStorage.getItem(key);
    if (saved === "1") setOn(true);
  }, [key]);
  const toggle = (v) => {
    const next = typeof v === "boolean" ? v : !on;
    setOn(next);
    localStorage.setItem(key, next ? "1" : "0");
  };
  const ui = (
    <label className="inline-flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer select-none" data-testid={`show-photo-toggle-${scope}`}>
      <input type="checkbox" checked={on} onChange={e => toggle(e.target.checked)} className="accent-sky-600" />
      Show Product Photo
    </label>
  );
  return [on, ui, toggle];
}

export default SkuImage;
